import React from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionTitle } from '@/components/common/SectionTitle';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Target, Users, Award, BookOpen, TrendingUp, Heart } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'Delivering high-quality education with consistent results'
    },
    {
      icon: Users,
      title: 'Student-Centric',
      description: 'Personalized attention and mentorship for every learner'
    },
    {
      icon: Award,
      title: 'Integrity',
      description: 'Ethical, transparent, and value-based education practices'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Dedicated educators committed to student success'
    }
  ];

  const milestones = [
    { year: '2021', event: 'Mantra Prayas Established in Bhilwara' },
    { year: '2022', event: 'First Major Results in JEE & NEET' },
    { year: '2023', event: '75% students got selection for JEE Advance' },
    { year: '2024–25', event: 'Multiple students got more than 99 %ile' }
  ];

  return (
    <div>
      <PageHeader
        title="About Mantra Prayas"
        subtitle="Creating an impact through excellence in education"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'About Us' }
        ]}
      />

      {/* Mission & Vision */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=800&h=600&fit=crop"
                alt="About Mantra Prayas"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <SectionTitle
                title="Our Story"
                centered={false}
                className="mb-6"
              />
              <div className="space-y-4 text-foreground leading-relaxed">
                <p>
                  Mantra Prayas (MP) is a premier coaching institute in Bhilwara dedicated to excellence in teaching and focused on interdisciplinary learning. Established with the vision of nurturing young talent, Mantra Prayas provides an intensive academic campus to guide students towards success in IIT-JEE, NEET, and various Olympiads.
                </p>
                <p>
                  What began as a focused initiative to deliver quality education has today evolved into a trusted name for competitive exam preparation. Our expert faculty, structured curriculum, and student-centric approach add zeal to students’ performance and help them qualify some of the toughest examinations in the country.
                </p>
                <p>
                  At Mantra Prayas, we believe in transformation — transforming preparation, confidence, and capability. We invite students to experience a disciplined, motivating, and result-oriented learning environment that empowers them to achieve their academic goals.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-8">
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-6">
                    <div className="text-3xl font-bold text-primary mb-2">1000+</div>
                    <div className="text-sm text-muted-foreground">Total Students</div>
                  </CardContent>
                </Card>
                <Card className="bg-secondary/5 border-secondary/20">
                  <CardContent className="p-6">
                    <div className="text-3xl font-bold text-secondary mb-2">72%</div>
                    <div className="text-sm text-muted-foreground">Selection Rate</div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section className="section-padding bg-surface-alt">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardContent className="p-8">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                    <Target className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Our Mission</h3>
                  <div className="space-y-4 text-foreground leading-relaxed">
                    <p className="text-foreground leading-relaxed">
                      With a dedicated faculty team and strong support staff, our mission is to continuously inspire and motivate students on the path to success. We aim to provide a platform where goals are achieved, skills are nurtured, and values are built.
                    </p>
                    <p>
                      Our mission is to enable young aspirants across the nation to achieve success in Olympiads, IIT-JEE, and NEET through structured learning, expert guidance, and a disciplined academic environment.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="h-full border-2 border-secondary/20 bg-gradient-to-br from-secondary/5 to-transparent">
                <CardContent className="p-8">
                  <div className="h-16 w-16 rounded-full bg-secondary/10 flex items-center justify-center mb-6">
                    <TrendingUp className="h-8 w-8 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-4">Our Vision</h3>
                  <div className="space-y-4 text-foreground leading-relaxed">
                    <ul className='list-disc pl-5'>
                      <li>To reach a strength of 1000+ students in Bhilwara</li>
                      <li>To be counted among the top education brands in the field</li>
                      <li>To achieve high selections with top ranks in competitive examinations</li>
                    </ul>                  
                    <p className="text-foreground leading-relaxed">
                      We envision becoming the most trusted and result-oriented coaching institute, known for transforming aspirations into achievements.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionTitle
            title="Our Core Values"
            description="The principles that guide everything we do"
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full text-center border-2 hover:border-primary transition-all duration-300 hover:shadow-lg group">
                  <CardContent className="p-8">
                    <div className="h-16 w-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <value.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="container-custom">
          <SectionTitle
            title="Our Journey"
            description="Major milestones in our 5-year legacy"
            className="mb-16"
          />

          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center mb-4 last:mb-0"
              >
                <div className="flex-1 text-right pr-8">
                  {index % 2 === 0 && (
                    <>
                      <div className="text-3xl font-heading font-bold text-primary mb-2">
                        {milestone.year}
                      </div>
                      <div className="text-foreground">{milestone.event}</div>
                    </>
                  )}
                </div>

                <div className="relative">
                  <div className="h-12 w-12 rounded-full bg-primary border-4 border-background shadow-lg flex items-center justify-center">
                    <Award className="h-6 w-6 text-primary-foreground" />
                  </div>
                  {index < milestones.length - 1 && (
                    <div className="absolute top-12 left-1/2 w-0.5 h-12 bg-primary/30 -translate-x-1/2" />
                  )}
                </div>

                <div className="flex-1 pl-8">
                  {index % 2 !== 0 && (
                    <>
                      <div className="text-3xl font-heading font-bold text-primary mb-2">
                        {milestone.year}
                      </div>
                      <div className="text-foreground">{milestone.event}</div>
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
